import { AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild } from '@angular/core';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MpTooltipDirective } from '../mp-tooltip/mp-tooltip.directive';
import { DomUtils } from '../../utils/dom.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomEventListenerService } from '../../services/dom-event-listener.service';

@Component({
  selector: 'app-mp-input',
  templateUrl: './mp-input.component.html',
  imports: [
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MpTooltipDirective,
    NgClass
  ],
  styleUrl: './mp-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MpInputComponent implements OnInit, OnChanges, AfterViewInit {

  /** 'text' | 'number' */
  @Input() public type: 'text' | 'number' | 'password' = 'text';
  /** 'small' | 'medium' | 'large' */
  @Input() public size: 'small' | 'medium' | 'large' = 'large';
  @Input() public step: number;
  @Input() public pattern: string;
  @Input() public onBlur: (input: unknown) => void = (input: unknown) => {};
  @Input() public outlined: boolean = false;
  @Input() public customClasses: string = '';
  @Input() public faIcon: string;
  @Input() public iconDisabled: boolean;
  @Input() public label: string = '';
  @Input() public disabled: boolean;
  @Input() public isReadOnly: boolean = false;
  @Input() public inputValue: unknown;
  @Input() public formControlField: FormControl;
  @Input() public overflowTooltipAllowed: boolean = false;
  @Input() public requestedFocus: boolean = false;
  @Input() public allowResetValue: boolean = false;

  @Output() public iconClicked = new EventEmitter<void>();
  @Output() public inputValueChange = new EventEmitter<unknown>();

  @ViewChild('formControlInputField', { static: false }) public formControlInputField: ElementRef;
  @ViewChild('valueInputField', { static: false }) public valueInputField: ElementRef;

  private valueChangeTimer: ReturnType<typeof setTimeout>;
  private _destroyRef = inject(DestroyRef);

  protected isInvalidInput: boolean = false;

  constructor(private domEventListener: DomEventListenerService) {}

  ngOnInit() {
    if (this.formControlField) {
      this.formControlField.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        this.onValueChanged();
      });

      this.formControlField.statusChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        this.isInvalidInput = this.formControlField?.invalid && this.formControlField?.dirty;
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['requestedFocus'] && this.requestedFocus) {
      this.focusInput();
    }
  }

  ngAfterViewInit() {
    if (this.requestedFocus) {
      this.focusInput();
    }

    this.domEventListener.onScrollObservable.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.valueInputField?.nativeElement.blur();
      this.formControlInputField?.nativeElement.blur();
    });
  }

  protected isEllipsisActive() {
    return DomUtils.isEllipsisActive(this.formControlInputField?.nativeElement) || DomUtils.isEllipsisActive(this.valueInputField?.nativeElement);
  }

  public onValueChanged() {
    if (this.valueChangeTimer) {
      clearTimeout(this.valueChangeTimer);
    }

    if (this.disabled) {
      return;
    }

    this.valueChangeTimer = setTimeout(() => {
      if (!this.formControlField) {
        this.inputValueChange.emit(this.inputValue);
      } else {
        this.inputValueChange.emit(this.formControlField.value);
      }
    }, 300);
  }

  public iconClick() {
    if (!this.iconDisabled) {
      this.iconClicked.emit();
    }
  }

  public focusInput() {
    if (this.formControlInputField?.nativeElement) {
      this.formControlInputField.nativeElement.focus();
    } else if (this.valueInputField?.nativeElement) {
      this.valueInputField.nativeElement.focus();
    }
  }

  public blurInput() {
    if (this.formControlInputField?.nativeElement) {
      this.formControlInputField.nativeElement.blur();
    } else if (this.valueInputField?.nativeElement) {
      this.valueInputField.nativeElement.blur();
    }
  }

  public resetValue(event: Event) {
    event.stopPropagation();
    this.inputValueChange.emit('');
  }

  protected canResetValue(): boolean {
    return this.inputValue && this.allowResetValue;
  }

  protected invalidInputErrorMsg() {
    const firstKey = Object.keys(this.formControlField?.errors)[0];
    return this.formControlField?.errors[firstKey];
  }
}
